import type { LooseRelationship } from '../../../../../types/electron';
import { evaluateFilterGroupsAgainstRow } from '../../utils/filterEvaluator';

export type CellEditVariant = 'plain' | 'enum' | 'fk' | 'boolean';

export type CellEditFkTarget = {
  schema: string;
  table: string;
  column: string;
  isLoose: boolean;
};

export type CellEditVariantResult = {
  variant: CellEditVariant;
  /**
   * One entry per source-filter-matching loose declaration. Populated for
   * variant='fk' with isLoose=true, and for variant='enum' when a declared
   * enum outranked a loose FK (the editor's row-picker escape).
   */
  looseMatches?: readonly LooseRelationship[];
  /**
   * The FK target the cell points at; for loose this is the first match's
   * target. Populated for variant='fk', and ALSO for variant='enum' when the
   * column would otherwise have resolved to 'fk' — the enum editor renders it
   * as a "Search rows in …" footer row so the full row picker stays one click
   * away. Absent on an enum result means "no reachable FK target".
   */
  fkTarget?: CellEditFkTarget;
  /**
   * For variant='boolean' or variant='enum'. Drives whether the picker
   * offers a NULL option and whether Ctrl+0 in the editor commits null.
   * Defaults to `true` when nullability is unknown — conservative choice.
   */
  nullable?: boolean;
};

export type CellEditVariantInput = {
  columnId: string;
  dataType: string | undefined;
  isRealForeignKey: boolean;
  realFkTarget?: { schema: string; table: string; column: string };
  declaredEnumColumnIds: ReadonlySet<string>;
  nativeEnumColumnIds: ReadonlySet<string>;
  /**
   * All loose-rel candidates for this column (pre source-filter eval).
   * Rels carrying a `sourcePath` are ignored — they address a JSON sub-key,
   * not the cell (see `resolveFkResolution`) — so callers need not pre-filter.
   */
  looseRelCandidates: readonly LooseRelationship[];
  /** Full row data for evaluating each loose rel's sourceFilter. Undefined while editing a brand-new row. */
  sourceRow: Record<string, unknown> | undefined;
  /** Column nullability — drives whether the boolean picker offers a NULL option. Defaults to nullable. */
  nullable?: boolean;
};

/**
 * Recognises engine-level boolean types across all five supported engines:
 *   - MSSQL: `bit`
 *   - Postgres: `boolean`, `bool`
 *   - MySQL: `tinyint(1)` (boolean convention), `bool`, `boolean`
 *   - Oracle: `number(1,0)` is sometimes used; we don't auto-detect this
 *     because it's also a legitimate signed-digit numeric. Users can declare
 *     such columns as enums for the toggle behaviour.
 *   - SQLite: `boolean` (affinity)
 *
 * Guard against false positives like `bigint` (contains "int" but not a
 * boolean) or `biteshmunge` (starts with "bit" but isn't a type). We accept
 * only:
 *   - exact match: `bit`, `bool`, `boolean`
 *   - `tinyint(1)` (MySQL boolean convention)
 */
function isBooleanType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const dt = dataType.toLowerCase().trim();
  if (dt === 'bit' || dt === 'bool' || dt === 'boolean') return true;
  if (dt === 'tinyint(1)') return true;
  return false;
}

/**
 * The FK target this column points at, if any: the real constraint first, then
 * the loose declarations whose `sourceFilter` matches the row being edited.
 * `undefined` = nothing reachable (no FK, or every loose rel is filtered out
 * for this particular row).
 *
 * **Loose rels with a `sourcePath` are excluded outright.** Such a rel points
 * from a value INSIDE a JSON cell (`$.owner_id`), not from the cell — so it
 * can never resolve what editing the whole cell means, and letting it try
 * hands the user a row picker that would commit a scalar over their JSON
 * document. Both row-details panels already strip them before calling in
 * (`RowDetailsDataTab.tsx`, `RowDetailsPanel.tsx`); the two grids do not, so
 * the exclusion belongs in the shared decision instead of at each call site.
 */
function resolveFkResolution(input: CellEditVariantInput):
  | { fkTarget: CellEditFkTarget; looseMatches?: readonly LooseRelationship[] }
  | undefined {
  if (input.isRealForeignKey && input.realFkTarget) {
    return { fkTarget: { ...input.realFkTarget, isLoose: false } };
  }

  const wholeCellCandidates = input.looseRelCandidates.filter((rel) => !rel.sourcePath);
  if (wholeCellCandidates.length === 0) return undefined;
  // No source row (a brand-new row being composed) means no filter can be
  // evaluated — offer every candidate rather than none.
  const matches = input.sourceRow
    ? wholeCellCandidates.filter((rel) =>
        evaluateFilterGroupsAgainstRow(input.sourceRow!, rel.sourceFilter),
      )
    : [...wholeCellCandidates];
  if (matches.length === 0) return undefined;

  const first = matches[0];
  return {
    looseMatches: matches,
    fkTarget: {
      schema: first.toSchema,
      table: first.toTable,
      column: first.toColumn,
      isLoose: true,
    },
  };
}

/**
 * Pure decision shared by both grids. Precedence:
 *   1. Array-typed → 'plain' (array cells are read-only).
 *   2. **Declared** enum → 'enum' (carrying the FK target as an escape hatch).
 *   3. Real FK → 'fk' (constraint target).
 *   4. Loose FK with at least one source-filter-matching declaration → 'fk'.
 *   5. Native enum → 'enum'.
 *   6. Boolean engine type → 'boolean'.
 *   7. Otherwise → 'plain'.
 *
 * **The declared-enum-over-FK flip (lookup enums).** This used to be the other
 * way round, justified as "a declared reference is a stronger signal than a
 * values heuristic". That rationale no longer holds: a *declared* enum is not
 * a heuristic — it is the same kind of explicit user statement the FK is, and
 * on an FK column it is specifically the statement *"this column has few
 * enough values that I want to pick one from a list"*. For those columns the
 * lightweight labelled enum picker (a short list of `Active ·3` rows, opened
 * and committed without leaving the cell) is strictly better than a modal row
 * search — and nothing is lost, because the enum result carries `fkTarget` /
 * `looseMatches` so `EnumCellEditor` keeps the full row picker one click away
 * in its footer. A **native** enum stays *below* FK: the engine's type is not
 * a statement the user made about this column.
 *
 * Enum still wins over boolean, for the same reason it now wins over FK: a
 * user declaring a bit/bool column as an enum has overridden the default
 * toggle behaviour on purpose.
 */
export function resolveCellEditVariant(input: CellEditVariantInput): CellEditVariantResult {
  if (input.dataType && input.dataType.endsWith('[]')) return { variant: 'plain' };

  const fk = resolveFkResolution(input);

  if (input.declaredEnumColumnIds.has(input.columnId)) {
    return { variant: 'enum', nullable: input.nullable ?? true, ...(fk ?? {}) };
  }

  if (fk) return { variant: 'fk', ...fk };

  if (input.nativeEnumColumnIds.has(input.columnId)) {
    return { variant: 'enum', nullable: input.nullable ?? true };
  }

  if (isBooleanType(input.dataType)) {
    return { variant: 'boolean', nullable: input.nullable ?? true };
  }

  return { variant: 'plain' };
}
